import { LinkIcon } from '@primer/octicons-react';
import React, { Suspense } from 'react';
import { graphql } from 'react-relay';

import useLazyLoadQuery from '../../../hooks/useLazyLoadQuery';
import { TaskDerivedTasksDialog_derivedTasksQuery } from '../../../relay/__generated__/TaskDerivedTasksDialog_derivedTasksQuery.graphql';
import Dialog, { DialogProps } from '../../core/Dialog';
import LinkButton from '../../core/LinkButton';
import Spinner from '../../core/Spinner';
import Stack from '../../core/Stack';
import Text from '../../core/Text';
import View from '../../core/View';
import TaskStatusLabel from '../TaskStatusLabel';

const derivedTasksForTaskDerivedTasksDialog = graphql`
  query TaskDerivedTasksDialog_derivedTasksQuery($id: ID!, $kind: String) {
    task(id: $id) {
      id
      derivedTasks(kind: $kind) {
        id
        sequence
        title
        ...TaskStatusLabel_task
      }
    }
  }
`;

type Props = {
  taskId: string;
  kind: string;
  title: string;
  problemGeneratorId?: string;
} & DialogProps;

const TaskDerivedTasksDialog = ({ taskId, kind, title, problemGeneratorId, ...props }: Props) => (
  // 목록 길이에 맞춰 넓어지도록 (제목·상태가 줄바꿈되지 않게)
  <Dialog sx={{ width: ['100vw', '100vw', 'fit-content'], minWidth: [0, 0, 440], maxWidth: '100vw' }} {...props}>
    <Dialog.Header>
      <Text sx={{ fontSize: 3, fontWeight: 'bold' }}>{title}</Text>
    </Dialog.Header>
    <Dialog.Body>
      {problemGeneratorId ? (
        <LinkButton
          href={`/problemGenerator/${problemGeneratorId}`}
          size={'small'}
          variant={'invisible'}
          leadingIcon={LinkIcon}
        >
          자동 생성기로 이동
        </LinkButton>
      ) : null}
      {props.isOpen ? (
        <Suspense
          fallback={
            <View padding={2}>
              <Spinner size={'small'} />
            </View>
          }
        >
          <DerivedTaskList taskId={taskId} kind={kind} />
        </Suspense>
      ) : null}
    </Dialog.Body>
  </Dialog>
);

const DerivedTaskList = ({ taskId, kind }: { taskId: string; kind: string }) => {
  const [{ task }] = useLazyLoadQuery<TaskDerivedTasksDialog_derivedTasksQuery>(
    derivedTasksForTaskDerivedTasksDialog,
    { id: taskId, kind },
    { fetchPolicy: 'store-and-network' },
  );

  return task.derivedTasks.length > 0 ? (
    <>
      {task.derivedTasks.map((derivedTask) => (
        <Stack key={derivedTask.id} gapX={2}>
          <Stack.Item>
            <LinkButton
              href={`/task/${derivedTask.id}`}
              size={'small'}
              variant={'invisible'}
              leadingIcon={LinkIcon}
            >
              {derivedTask.sequence}
            </LinkButton>
          </Stack.Item>
          <Stack.Item>
            <Text fontSize={1} sx={{ whiteSpace: 'nowrap' }}>
              {derivedTask.title}
            </Text>
          </Stack.Item>
          <Stack.Item>
            <TaskStatusLabel task={derivedTask} sx={{ whiteSpace: 'nowrap' }} />
          </Stack.Item>
        </Stack>
      ))}
    </>
  ) : (
    <Text fontSize={1} color={'fg.muted'}>
      문항이 없어요
    </Text>
  );
};

export default TaskDerivedTasksDialog;
